// The throughput panel's issuetype filter: which types are selected, and what a click does.
//
// Multi-select rather than a single-choice dropdown, matching the Complexity filter beside
// it — "Stories and Tasks, not subtasks" is the actual question people ask of this chart,
// and a one-of-N control can't express it. Same chrome as Complexity too (`sr-checks`), so
// the section has one idiom for "filter by a discovered set of values" instead of two.
//
// The state is a comma-separated list of canonical keys, with '' meaning NO FILTER — the
// same convention `state.ca.complexity` uses, and for the same reason: an all-selected
// filter and an absent one must serialize identically, or the URL and the request grow a
// filter that isn't filtering.
//
// Pure, so the two rules that are easy to get wrong are testable directly: options are
// discovered per window, so a selection can outlive the option it names; and "everything
// selected" has to collapse to '' rather than being sent as an explicit list.

/** One selectable type, as the server discovered it for this window and scope. */
export interface ItypeOption {
  key: string
  label: string
  count: number
}

/** Parse the stored filter into keys. '' (no filter) yields []. */
export function itypeSel(stored: string): string[] {
  return stored ? stored.split(',').filter(Boolean) : []
}

/**
 * Is `key` checked, given the stored filter and the options on offer?
 *
 * An empty filter means every type, so every box is checked — the control opens showing
 * what the chart is actually showing, and the first click therefore *narrows* rather than
 * appearing to reveal something that was hidden.
 */
export function itypeChecked(stored: string, key: string): boolean {
  const sel = itypeSel(stored)
  return sel.length === 0 || sel.indexOf(key) >= 0
}

/**
 * The stored filter after clicking `key`, over the options currently offered.
 *
 * Two rules, both there to keep "no filter" and "all types" the same state:
 *   * unchecking from the implicit-all state selects every OTHER type, which is what the
 *     user just expressed — not an empty selection;
 *   * re-selecting everything collapses back to '', so the request carries no filter and
 *     the panel's note can honestly say it isn't filtered.
 *
 * Unchecking the last remaining type is refused (returns the filter unchanged). An empty
 * selection is a legitimate state for the STORE to answer — it means zero — but as a UI
 * state it is a dead end: an empty chart with every box unchecked and no hint of which one
 * to click. Complexity keeps at least one box checked for the same reason.
 */
export function itypeToggle(stored: string, key: string, options: ItypeOption[]): string {
  const all = options.map((o) => o.key)
  const cur = itypeSel(stored)
  const sel = cur.length === 0 ? all.slice() : cur.filter((k) => all.indexOf(k) >= 0)
  const at = sel.indexOf(key)
  let next: string[]
  if (at >= 0) {
    if (sel.length === 1) return stored // never leave nothing selected
    next = sel.filter((k) => k !== key)
  } else {
    next = sel.concat([key])
  }
  // Preserve the options' own order (count-descending from the server) rather than click
  // order, so the serialized value is stable — the same selection reached two ways is one
  // string, which keeps it usable as a cache key and comparable across renders.
  const ordered = all.filter((k) => next.indexOf(k) >= 0)
  return ordered.length === all.length ? '' : ordered.join(',')
}

/**
 * Drop selected keys that this window's options no longer contain, and collapse a
 * now-complete selection to ''.
 *
 * The options are discovered per window and scope, so widening from 7 days to 90 can add a
 * type and narrowing can remove one. A stale key would then filter on something the chart
 * has no rows for — the panel would sit empty with a checkbox row that doesn't explain why.
 * Returns '' when nothing recognisable survives, i.e. fall back to showing everything: the
 * chart the user can see beats a filter they can no longer see.
 */
export function itypeReconcile(stored: string, options: ItypeOption[]): string {
  const sel = itypeSel(stored)
  if (!sel.length) return ''
  const all = options.map((o) => o.key)
  const kept = all.filter((k) => sel.indexOf(k) >= 0)
  if (!kept.length || kept.length === all.length) return ''
  return kept.join(',')
}

/**
 * The panel subtitle's filter clause — '' when unfiltered.
 *
 * Named types rather than a count ("Story, Task", not "2 of 4 types"): the chart's height
 * changes with the filter, so what it is counting has to be legible from the panel itself,
 * not only from the checkbox row's state.
 */
export function itypeSubLabel(stored: string, options: ItypeOption[]): string {
  const sel = itypeSel(stored)
  if (!sel.length) return ''
  const labels = options.filter((o) => sel.indexOf(o.key) >= 0).map((o) => o.label)
  if (!labels.length) return ''
  return labels.join(', ') + ' only'
}
